import { useEffect, useMemo, useRef, useState } from 'react';
import { IconSearch, IconX } from '@tabler/icons-react';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import { loadEmojiGroups, searchEmoji } from '../engine/emojiCatalogue';

/** Drawn well above the page it will land on, so a stretched stamp stays crisp. */
const RASTER_SIZE = 256;

/**
 * Emoji picker, in the shape the word processors use: a search box, a row of
 * category tabs, and a grid.
 *
 * What it hands back is a transparent PNG, not a character. A PDF has to carry
 * the glyph with it, and the fonts this editor embeds have no emoji in them —
 * a pasted character would export as an empty box on any reader whose own fonts
 * disagree. Rasterising at {@link RASTER_SIZE} sidesteps the whole question:
 * what you picked is what the file contains.
 */
export default function EmojiModal({ onClose, onApply }) {
    const { t } = useTranslation();
    const [query, setQuery] = useState('');
    const [groups, setGroups] = useState([]);
    const [group, setGroup] = useState(null);
    const searchRef = useRef(null);

    // The catalogue is ~400KB of JSON, so it arrives on first open rather than
    // riding in the main bundle; until it does the grid is simply empty.
    useEffect(() => {
        let alive = true;
        loadEmojiGroups().then((loaded) => {
            if (!alive) return;
            setGroups(loaded);
            setGroup((current) => current ?? loaded[0]?.id ?? null);
        });
        return () => { alive = false; };
    }, []);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
        window.addEventListener('keydown', onKey, true);
        requestAnimationFrame(() => searchRef.current?.focus());
        return () => window.removeEventListener('keydown', onKey, true);
    }, [onClose]);

    const shown = useMemo(() => {
        const q = query.trim();
        if (q) return searchEmoji(groups, q);
        return groups.find((g) => g.id === group)?.emoji ?? [];
    }, [query, group, groups]);

    /** Paint the glyph on its own and hand back a PNG the page can hold. */
    const pick = (emoji) => {
        const canvas = document.createElement('canvas');
        canvas.width = RASTER_SIZE;
        canvas.height = RASTER_SIZE;
        const ctx = canvas.getContext('2d');
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        // The system emoji face, whichever this machine has; the size leaves a
        // little room so nothing is clipped by a tall glyph.
        ctx.font = `${Math.round(RASTER_SIZE * 0.78)}px "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif`;
        ctx.fillText(emoji.char, RASTER_SIZE / 2, RASTER_SIZE / 2 + RASTER_SIZE * 0.04);
        onApply({ src: canvas.toDataURL('image/png'), width: RASTER_SIZE, height: RASTER_SIZE });
        onClose();
    };

    return (
        <div
            className="fixed inset-0 z-[9200] flex items-center justify-center bg-[#1C1815]/45 p-4"
            role="presentation"
            onClick={onClose}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-label={t('pdfEditor.emoji.title')}
                onClick={(e) => e.stopPropagation()}
                className="flex max-h-[min(560px,90vh)] w-full max-w-[420px] flex-col overflow-hidden rounded-2xl border border-[#ECE8E4] bg-white shadow-[0_20px_50px_rgba(28,24,21,0.25)] dark:border-zinc-700 dark:bg-zinc-900"
            >
                <div className="flex items-center gap-2 border-b border-[#ECE8E4] px-3 py-2.5 dark:border-zinc-700">
                    <div className="relative flex-1">
                        <IconSearch size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#A8A099] dark:text-zinc-500" />
                        <input
                            ref={searchRef}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder={t('pdfEditor.emoji.search')}
                            className="h-9 w-full rounded-lg border border-[#ECE8E4] bg-white pl-8 pr-2 text-[13px] text-[#1C1815] outline-none transition-colors placeholder:text-[#A8A099] focus:border-amber-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
                        />
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label={t('common.close')}
                        className="flex h-8 w-8 flex-none items-center justify-center rounded-lg text-[#A8A099] transition-colors hover:bg-[#F7F5F3] hover:text-[#3D3833] dark:hover:bg-zinc-800"
                    >
                        <IconX size={16} stroke={2} />
                    </button>
                </div>

                {/* The tabs are only a way of jumping around the catalogue, so a
                    search — which spans all of it — leaves them out of the way. */}
                {!query.trim() ? (
                    <div className="flex gap-0.5 border-b border-[#ECE8E4] px-2 py-1.5 dark:border-zinc-700">
                        {groups.map((g) => (
                            <button
                                key={g.id}
                                type="button"
                                onClick={() => setGroup(g.id)}
                                title={g.name}
                                aria-label={g.name}
                                aria-pressed={group === g.id}
                                className={`flex h-8 flex-1 items-center justify-center rounded-md text-[16px] leading-none transition-colors ${
                                    group === g.id
                                        ? 'bg-[#F7F5F3] dark:bg-zinc-800'
                                        : 'opacity-55 hover:opacity-100'
                                }`}
                            >
                                {g.icon}
                            </button>
                        ))}
                    </div>
                ) : null}

                <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
                    {!query.trim() ? (
                        <p className="px-1 pb-1.5 text-[10.5px] font-semibold uppercase tracking-wide text-[#A8A099] dark:text-zinc-500">
                            {groups.find((g) => g.id === group)?.name}
                        </p>
                    ) : null}
                    {shown.length ? (
                        <div className="grid grid-cols-9 gap-0.5">
                            {shown.map((emoji) => (
                                <button
                                    key={emoji.char}
                                    type="button"
                                    onClick={() => pick(emoji)}
                                    title={emoji.name}
                                    aria-label={emoji.name}
                                    className="flex aspect-square items-center justify-center rounded-md text-[20px] leading-none transition-colors hover:bg-[#F7F5F3] dark:hover:bg-zinc-800"
                                >
                                    {emoji.char}
                                </button>
                            ))}
                        </div>
                    ) : (
                        <p className="px-1 py-8 text-center text-[12.5px] text-[#A8A099] dark:text-zinc-500">
                            {t('pdfEditor.emoji.noMatch')}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}
